import {noop} from 'lodash';
import {BehaviorSubject} from 'rxjs';
import {Subscription, useSubscription} from 'use-subscription';

import {isMobile} from '../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {reportToBugsnag} from '../../srcCommon/services/bugsnag/bugsnag';
import {PersistedStorageUnsupportedError} from '../../srcCommon/services/persistentState/persistedState';

/*
 * State.
 */

// Holds the most recently known state of persisted storage.
let hasPersistedStorage$ = new BehaviorSubject<boolean>(isMobile());

/*
 * Helpers.
 */

let hasPersistedStoragePromise: Promise<boolean> | undefined;

export async function hasPersistedStorageAsync() {
  if (isMobile()) {
    hasPersistedStoragePromise = Promise.resolve(true);
  }

  if (!hasPersistedStoragePromise) {
    // Ask the browser, if it supports the Storage API.
    hasPersistedStoragePromise =
      navigator.storage && navigator.storage.persisted
        ? navigator.storage.persisted()
        : Promise.resolve(false);

    // As a side-effect, update the state and report errors.
    hasPersistedStoragePromise.then(
      (hasPersistedStorage) => {
        hasPersistedStorage$.next(hasPersistedStorage);
        return hasPersistedStorage;
      },
      (error) => {
        reportToBugsnag(error);
        return false;
      },
    );
  }

  return hasPersistedStoragePromise;
}

// TODO: Add a helper to request persisted storage.

/** Reset this module's state for testing purposes. */
export function resetPersistedStorage() {
  hasPersistedStorage$ = new BehaviorSubject<boolean>(false);
  hasPersistedStoragePromise = undefined;
}

export async function assertPersistedStorageAsync() {
  const hasPersistedStorage = await hasPersistedStorageAsync();
  if (!hasPersistedStorage) {
    throw new PersistedStorageUnsupportedError();
  }
}

/*
 * Hooks.
 */

// Shared by all users of useHasPersistedStorage().
const hasPersistedStorageSubscription: Subscription<boolean> = {
  getCurrentValue: () => hasPersistedStorage$.getValue(),
  subscribe: (callback) => {
    const subscription = hasPersistedStorage$.subscribe(callback);
    return () => subscription.unsubscribe();
  },
};

export function useHasPersistedStorage() {
  // Make sure we have updated the state at least once.
  hasPersistedStorageAsync().catch(noop);

  return useSubscription(hasPersistedStorageSubscription);
}
